/**
 * ConverterActivityPanel — "what have I done on this project" timeline for
 * the Data Converter agent.
 *
 * Reads from the EXISTING generic project-activity endpoint
 * (`apiv2.getProjectActivity`, backed by `AgentSession` — CLAUDE.md's
 * "Universal Session Tracking": AgentSession is the single source of truth
 * for all agent activity, never a bespoke per-agent table) and filters
 * client-side to `session_type` values prefixed `data_converter.` (written
 * by ztd_app/api/modules/data_converter/router.py's `_log_if_project`/
 * `_log_activity` helpers). No new backend read endpoint — this is purely a
 * presentational, Data-Converter-scoped view over data that already exists
 * application-wide.
 */

import { useCallback, useEffect, useState } from "react";
import {
  RefreshCcw,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Ban,
  Database,
  Cloud,
  Link,
  FileSpreadsheet,
  Upload,
  Scale,
  Sparkles,
} from "lucide-react";
import { apiv2, type AgentSessionSummary } from "../apiv2";

const _PREFIX = "data_converter.";

const ACTION_LABELS: Record<string, string> = {
  source_connect: "Connected source DB",
  target_connect: "Connected target DB",
  schema_scan: "Scanned schema",
  mapping_generate: "Generated mapping",
  mapping_import: "Imported mapping document",
  mapping_save: "Saved mapping",
  sttm_generate: "Generated STTM workbook",
  ddl_generate: "Generated DDL",
  ddl_execute: "Executed DDL",
  data_load: "Load Data",
  reconciliation: "Reconciliation",
  target_platform_connect: "Connected target platform",
  target_platform_objects: "Loaded target objects",
  target_platform_connection_save: "Saved target platform connection",
  load_to_target: "Load to Target",
  skills_update: "Updated functional skills",
};

const ACTION_ICONS: Record<string, React.ReactNode> = {
  source_connect: <Database size={13} />,
  target_connect: <Database size={13} />,
  schema_scan: <Database size={13} />,
  mapping_generate: <Link size={13} />,
  mapping_import: <Upload size={13} />,
  mapping_save: <Link size={13} />,
  sttm_generate: <FileSpreadsheet size={13} />,
  ddl_generate: <Database size={13} />,
  ddl_execute: <Database size={13} />,
  data_load: <Upload size={13} />,
  reconciliation: <Scale size={13} />,
  target_platform_connect: <Cloud size={13} />,
  target_platform_objects: <Cloud size={13} />,
  target_platform_connection_save: <Cloud size={13} />,
  load_to_target: <Cloud size={13} />,
  skills_update: <Sparkles size={13} />,
};

function statusStyle(status: string): { cls: string; icon: React.ReactNode } {
  switch (status) {
    case "active":
    case "running":
      return {
        cls: "bg-sky-500/15 text-sky-300 border-sky-500/25",
        icon: <Loader2 size={11} className="animate-spin" />,
      };
    case "completed":
      return {
        cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/25",
        icon: <CheckCircle2 size={11} />,
      };
    case "cancelled":
      return {
        cls: "bg-white/5 text-white/40 border-white/10",
        icon: <Ban size={11} />,
      };
    case "error":
    case "failed":
      return {
        cls: "bg-red-500/15 text-red-300 border-red-500/25",
        icon: <AlertTriangle size={11} />,
      };
    default:
      return {
        cls: "bg-white/5 text-white/55 border-white/10",
        icon: <Clock size={11} />,
      };
  }
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffMs = Date.now() - then;
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

export default function ConverterActivityPanel({
  projectId,
}: {
  projectId: string;
}) {
  const [items, setItems] = useState<AgentSessionSummary[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiv2.getProjectActivity(projectId);
      const converterSessions = res.sessions
        .filter((s) => s.session_type.startsWith(_PREFIX))
        .sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        );
      setItems(converterSessions);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load activity");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-white/80">Recent Activity</h3>
        <button
          onClick={load}
          disabled={loading}
          className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/5 transition disabled:opacity-40"
          title="Refresh"
        >
          {loading ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <RefreshCcw size={13} />
          )}
        </button>
      </div>

      {error && <p className="text-xs text-red-400 mb-2">{error}</p>}

      {items === null && !error ? (
        <p className="text-xs text-white/30 italic">Loading…</p>
      ) : items && items.length === 0 ? (
        <p className="text-xs text-white/30 italic">
          No actions performed on this project yet — connect a source and
          generate a mapping to get started.
        </p>
      ) : (
        <ul className="space-y-1.5 max-h-80 overflow-y-auto">
          {(items ?? []).map((item) => {
            const action = item.session_type.slice(_PREFIX.length);
            const { cls, icon } = statusStyle(item.status);
            return (
              <li
                key={item.id}
                className="flex items-center gap-2.5 text-xs py-1.5 px-1 rounded-lg hover:bg-white/[0.03]"
              >
                <span className="text-white/40 flex-shrink-0">
                  {ACTION_ICONS[action] ?? <Database size={13} />}
                </span>
                <span className="flex-1 text-white/70 truncate">
                  {item.label || ACTION_LABELS[action] || action}
                </span>
                <span
                  className={`flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium flex-shrink-0 ${cls}`}
                >
                  {icon}
                  {item.status}
                </span>
                <span className="text-white/30 flex-shrink-0 w-14 text-right">
                  {formatRelative(item.created_at)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
